import React, { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import { UserService } from "../../../services/UserService";
import {
  DEVICE_TYPES as T,
  GATE_TYPES,
  MODELS,
  SENSOR_TYPES,
  isModel,
  typeOf,
} from "../../../constants/devices";
import { historyColor } from "../../../components/device/app/deviceTheme";
import { sensorHistoryLabel } from "../../../components/device/sensorStatus";

// Why a water pump motor or a socket switched off (info.trip), as in the app.
const MOTOR_TRIPS = {
  1: "High voltage protection",
  2: "Low voltage protection",
  3: "Over load protection",
  4: "Dry run protection",
  5: "Locked rotor protection",
  6: "Source tank empty",
  7: "Drain tank full",
  8: "Max run time elapsed",
  9: "Working hours elapsed",
  10: "Cyclic ON delay time elapsed",
  11: "ON delay time not complete",
  12: "By panel",
  13: "By app",
};
const SOCKET_TRIPS = { 1: "High Voltage Protection", 2: "Low Voltage Protection", 3: "Over Load Protection" };
export const VACUUM_STATUS = {
  0: "Standby",
  1: "Smart Clean",
  2: "Wall Clean",
  3: "Spot Clean",
  4: "Mop Clean",
  5: "Go to Charge",
  6: "Charging",
  7: "Charge Done",
  8: "Paused",
  9: "Cleaning",
  10: "Sleep Charging",
  11: "Direct Charged",
};

// Which id the history is stored under (app/screens/CommonSwichDetails.js).
export const historyIdOf = (device, motor = 1) => {
  const type = typeOf(device);
  if ([T.SWING_GATE, T.ROBOT_VACUUM_CLEANER, T.ENERGY_METER].includes(type)) return device.deviceId;
  if (type === T.WATER_LEVEL_CONTROLLER) return `${device.applianceId}-${motor}`;
  return device.applianceId;
};

const gateTitle = (device, info) => {
  const shutter = typeOf(device) === T.SHUTTER_GATE;
  const step = info.action ?? (info.state !== undefined ? Number(info.state) - 1 : null);
  if (step === null || step === undefined || step === "") return null;
  const words = shutter ? ["Shutter Up", "Shutter Down", "Shutter Stopped"] : ["Gate Opened", "Gate Closed", "Gate Stopped"];
  return words[Number(step)] ?? null;
};

// Row title for one history entry, per device type.
const entryTitle = (device, entry, motor) => {
  const type = typeOf(device);
  const info = entry?.info ?? {};
  if (SENSOR_TYPES.includes(type)) {
    const label = sensorHistoryLabel(type, entry);
    return type === T.DW_SENSOR ? label?.toUpperCase() : label;
  }
  if (GATE_TYPES.includes(type)) return gateTitle(device, info) ?? (entry.status ? "ON" : "OFF");
  if (type === T.ROBOT_VACUUM_CLEANER) {
    return `${VACUUM_STATUS[info.work_stat] ?? "Status"} (${info.clean_area ?? 0}m² | ${info.clean_time ?? 0}min)`;
  }
  if (type === T.ENERGY_METER) return info.alert ?? (entry.status ? "ON" : "OFF");
  if (type === T.SECURITY_ALARM) return info.content ?? info.alert ?? "Alert";
  if (type === T.WATER_LEVEL_CONTROLLER) {
    const reason = !entry.status && MOTOR_TRIPS[info.trip] ? ` - ${MOTOR_TRIPS[info.trip]}` : "";
    return `${entry.status ? "ON" : "OFF"} (Motor ${motor}${reason})`;
  }
  if (type === T.SOCKET && device.deviceCategoryId === "0x01" && !entry.status && SOCKET_TRIPS[info.trip]) {
    return `OFF (${SOCKET_TRIPS[info.trip]})`;
  }
  if (entry.status) return entry.speed ? `ON (${entry.speed})` : "ON";
  return "OFF";
};

const toInput = (m) => m.format("YYYY-MM-DDTHH:mm");

// The panel's title: some devices' history is called Recent Activities.
export const historyTitleOf = (device) =>
  ["0x0d", "0x04"].includes(device?.deviceCategoryId) ? "Recent Activities" : "Usage History";

// The app's Usage History sheet (app/screens/TimelineView.js): the last 24
// hours by default, or any range the user applies. tabs, when given, draws the
// panel's tabs in place of its title (the device page's Automations tab).
const UsageHistory = ({ device, motor = 1, tabs }) => {
  const [range, setRange] = useState(() => ({ from: toInput(moment().subtract(24, "hours")), to: toInput(moment()) }));
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState("");
  const latest = useRef(0);
  const id = historyIdOf(device, motor);
  const type = typeOf(device);
  const recent = ["0x0d", "0x04"].includes(device.deviceCategoryId);

  const load = useCallback(
    async (from, to) => {
      const request = ++latest.current;
      setEntries(null);
      try {
        const dates = [moment(from).utc().format(), moment(to).utc().format()];
        // A security alarm's history is the alerts it raised.
        const rows =
          type === T.SECURITY_ALARM ? await UserService.getSecurityLogs(device.deviceId, dates) : await UserService.getTimeline(id, dates);
        if (request !== latest.current) return;
        const list = (Array.isArray(rows) ? rows : [])
          .filter((row) => !(type === T.SWING_GATE && row.info?.rl !== undefined && String(row.info.rl) !== String(device.pin)))
          .sort((a, b) => new Date(b.time) - new Date(a.time));
        setEntries(list);
      } catch (err) {
        if (request === latest.current) setEntries([]);
      }
    },
    [id, type, device.pin, device.deviceId]
  );

  useEffect(() => {
    load(range.from, range.to);
    // Reload when the device or motor changes; Apply reloads a new range.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const apply = () => {
    if (range.from === range.to) {
      setError("Same dates not allowed");
      return;
    }
    if (moment(range.from).isAfter(moment(range.to))) {
      setError("Start date is greater");
      return;
    }
    setError("");
    load(range.from, range.to);
  };

  const now = toInput(moment());

  return (
    <section className="hx-history" aria-label={recent ? "Recent activities" : "Usage history"} style={{ "--hx-tl": historyColor(type) }}>
      <div className="hx-history-handle" aria-hidden="true" />
      <div className="hx-history-head">
        {tabs ?? <h2 className="hx-history-title">{historyTitleOf(device)}</h2>}
        <button type="button" className="hx-history-apply" onClick={apply}>
          Apply
        </button>
      </div>
      <div className="hx-history-range">
        <label htmlFor="hx-history-from">Start Datetime</label>
        <input
          id="hx-history-from"
          type="datetime-local"
          value={range.from}
          max={now}
          onChange={(event) => setRange({ ...range, from: event.target.value })}
        />
        <label htmlFor="hx-history-to">End Datetime</label>
        <input
          id="hx-history-to"
          type="datetime-local"
          value={range.to}
          max={now}
          onChange={(event) => setRange({ ...range, to: event.target.value })}
        />
      </div>
      {error ? (
        <p className="hx-history-error" role="alert">
          {error}
        </p>
      ) : null}

      {entries === null ? (
        <div style={{ display: "grid", gap: 12, marginTop: 16 }}>
          <div className="hx-skel" style={{ minHeight: 56 }} />
          <div className="hx-skel" style={{ minHeight: 56 }} />
          <div className="hx-skel" style={{ minHeight: 56 }} />
        </div>
      ) : entries.length ? (
        <ol className="hx-timeline">
          {entries.map((entry, index) => {
            const at = new Date(entry.time);
            return (
              <li className="hx-tl-item" key={`${entry.time}-${index}`}>
                <span className="hx-tl-time">{moment(at).format("h:mm a")}</span>
                <span className="hx-tl-rail" aria-hidden="true">
                  <span className="hx-tl-dot" />
                </span>
                <div className="hx-tl-body">
                  <div className="hx-tl-title">{entryTitle(device, entry, motor)}</div>
                  <div className="hx-tl-date">{at.toDateString()}</div>
                </div>
              </li>
            );
          })}
        </ol>
      ) : (
        <p className="hx-history-empty">{recent ? "No recent activities" : "No recent usage history"}</p>
      )}
    </section>
  );
};

// The app shows no history for dimmers and for the WGRVC_1 vacuum.
export const hasHistory = (device) => !(typeOf(device) === T.DIMMER_MODULE || isModel(device, MODELS.WGRVC_1));

export default UsageHistory;
